// src/components/layout/RoddoPageHero.tsx
import React from 'react';
import clsx from 'clsx';

import { getRoddoPageTone, type RoddoPageToneKey } from '../../constants/pageTones';
import { resolveRoddoDarkMode } from '../../constants/theme';

type HeroStat = {
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
};

interface RoddoPageHeroProps {
  tone?: RoddoPageToneKey;
  kicker?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  stats?: HeroStat[];
  actions?: React.ReactNode;
  topRightAction?: React.ReactNode;
  children?: React.ReactNode;
  compact?: boolean;
  className?: string;
  isDarkMode?: boolean;
}

const RoddoPageHero: React.FC<RoddoPageHeroProps> = ({
  tone = 'default',
  kicker,
  title,
  subtitle,
  icon,
  stats,
  actions,
  topRightAction,
  children,
  compact = false,
  className,
  isDarkMode,
}) => {
  const dark = resolveRoddoDarkMode(isDarkMode);
  const toneConfig = getRoddoPageTone(tone);
  const palette = dark ? toneConfig.dark : toneConfig.light;

  return (
    <section
      data-roddo-page-hero={toneConfig.key}
      className={clsx(
        'relative isolate overflow-hidden rounded-[18px] border',
        compact ? 'p-4' : 'p-5',
        'border-black/10 dark:border-[rgba(132,151,190,0.16)]',
        className,
      )}
      style={{
        background: dark ? '#101827' : '#FFFCF7',
        color: palette.text,
        boxShadow: dark
          ? '0 5px 14px rgba(0,0,0,0.14)'
          : '0 4px 14px rgba(89,65,37,0.06)',
      }}
    >
      <div
        className="pointer-events-none absolute inset-x-4 top-0 -z-10 h-px opacity-60"
        style={{ background: palette.accent }}
      />

      <div className="flex flex-col gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center rounded-full border border-black/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.20em] shadow-none dark:border-white/10"
                style={{ background: palette.chip, color: palette.chipText }}
              >
                {kicker ?? toneConfig.kicker}
              </span>
              <span
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-black/10 text-base shadow-none dark:border-white/10"
                style={{ background: palette.chip }}
                aria-hidden="true"
              >
                {icon ?? toneConfig.icon}
              </span>
            </div>

            {topRightAction ? (
              <div className="shrink-0">
                {topRightAction}
              </div>
            ) : null}
          </div>

          <h1 className="mt-3 text-[32px] font-black leading-[0.96] tracking-[-0.04em]">
            {title}
          </h1>

          {subtitle ? (
            <p className="mt-3 max-w-2xl text-[13px] font-semibold leading-6" style={{ color: palette.body }}>
              {subtitle}
            </p>
          ) : null}
        </div>

        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>

      {stats?.length ? (
        <div className="mt-5 grid grid-cols-2 gap-2">
          {stats.map((stat) => (
            <div
              key={`${stat.label}-${String(stat.value)}`}
              className="rounded-[12px] border border-black/10 px-3 py-3 shadow-none dark:border-[rgba(132,151,190,0.12)]"
              style={{ background: palette.chip }}
            >
              <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: palette.body }}>
                {stat.label}
              </div>
              <div className="mt-1 min-w-0 truncate text-lg font-black" style={{ color: palette.text }}>
                {stat.value}
              </div>
              {stat.helper ? (
                <div className="mt-1 min-w-0 truncate text-[11px] font-bold" style={{ color: palette.body }}>
                  {stat.helper}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {children ? <div className="mt-4">{children}</div> : null}
    </section>
  );
};

export default RoddoPageHero;
